import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, BookOpen, Loader2, Sparkles, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_CHIP = {
  recommended: "border-teal-600/50 text-teal-800 bg-teal-50/60",
  enrolled: "border-slate-400 text-slate-700 bg-slate-50",
  in_progress: "border-amber-500/60 text-amber-800 bg-amber-50",
  completed: "border-slate-500 text-slate-700 bg-slate-100",
  certified: "border-teal-700 text-white bg-teal-700",
};

const Gauge = ({ value, label, testId }) => {
  const r = 52, c = Math.PI * r;
  const filled = (Math.min(100, value) / 100) * c;
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <svg width="140" height="82" viewBox="0 0 140 82">
        <path d="M 18 74 A 52 52 0 0 1 122 74" fill="none" stroke="#E2E8F0" strokeWidth="9" />
        <path d="M 18 74 A 52 52 0 0 1 122 74" fill="none" stroke="var(--brand-accent)" strokeWidth="9"
          strokeDasharray={`${filled} ${c}`} style={{ transition: "stroke-dasharray 0.6s ease" }} />
        <text x="70" y="66" textAnchor="middle" fill="var(--brand-primary)" fontFamily="Inter, sans-serif" fontSize="22" fontWeight="700">
          {`${Math.round(value || 0)}%`}
        </text>
      </svg>
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground text-center max-w-[150px]">
        {label}
      </p>
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <Card className="rounded-none border-border shadow-sm" data-testid={testId}>
    <CardHeader className="pb-2 border-b border-border">
      <CardTitle className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)] flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-teal-600" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-4">{children}</CardContent>
  </Card>
);

export default function GrowthDashboard({ employeeId, showHeader = true }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [advisor, setAdvisor] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);

  const load = useCallback(() => {
    api.get(`/growth/${employeeId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const askAdvisor = async () => {
    setAdvisorLoading(true);
    try {
      const { data: res } = await api.post(`/growth/${employeeId}/advisor`);
      setAdvisor(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAdvisorLoading(false);
    }
  };

  if (error) return <div className="border border-border bg-white p-8 text-center text-[13px] text-slate-700">{error}</div>;
  if (!data) return <Skeleton className="h-96 w-full rounded-none" />;

  const topPathway = data.pathways[0];

  return (
    <div className="space-y-6 fade-in" data-testid="growth-dashboard">
      {showHeader && (
        <div className="bg-white border border-border p-6 flex items-center gap-5">
          <div className="h-14 w-14 bg-[var(--brand-primary)] text-white flex items-center justify-center text-lg font-bold shrink-0">
            {data.employee.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-[var(--brand-primary)]" data-testid="growth-employee-name">{data.employee.name}</h2>
            <p className="text-[13px] text-muted-foreground">
              {data.employee.title} · {data.employee.department} · joined {data.employee.join_date}
            </p>
          </div>
          <Button onClick={askAdvisor} disabled={advisorLoading} data-testid="growth-advisor-btn"
            className="rounded-none bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px]">
            {advisorLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            AI Growth Advisor
          </Button>
        </div>
      )}

      {advisor && (
        <div className="border border-teal-600/40 bg-teal-50/40 p-5 fade-in" data-testid="growth-advisor-note">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-teal-700" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-800">Growth Advisor</span>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto">
              masked via gateway · audit {advisor.audit_id.slice(0, 8)} · {advisor.model}
            </span>
          </div>
          <p className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap">{advisor.note}</p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <SectionCard title="Readiness — Top Pathways" icon={TrendingUp} testId="growth-pathways">
          <div className="flex justify-around">
            {data.pathways.map((p) => (
              <Gauge key={p.role} value={p.readiness} label={p.role} testId={`gauge-${p.role.toLowerCase().replace(/\s+/g, "-")}`} />
            ))}
          </div>
        </SectionCard>

        <SectionCard title={`Requirements — ${topPathway?.role || "Pathway"}`} testId="growth-requirements">
          <div className="space-y-3">
            {(topPathway?.breakdown || []).map((b) => (
              <div key={b.skill} className="flex items-center gap-3">
                <span className="w-40 text-[12px] text-slate-600 shrink-0 truncate">{b.skill}</span>
                <div className="flex-1 h-2 bg-slate-100 relative">
                  <div className="h-2 bg-teal-600" style={{ width: `${(b.effective / 5) * 100}%` }} />
                  <div className="absolute top-[-3px] h-[14px] w-px bg-[var(--brand-primary)]" style={{ left: `${(b.required / 5) * 100}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-500 w-16 text-right">{b.effective}/{b.required}</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground">Teal = effective proficiency (incl. adjacent skills) · marker = required level</p>
          </div>
        </SectionCard>

        <SectionCard title="Projected Uplift" icon={TrendingUp} testId="growth-uplift">
          {data.projected_uplift.uplifts.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">No queued courses — uplift appears when training is queued.</p>
          ) : (
            <div className="space-y-2.5">
              <p className="text-[12px] text-slate-600">
                Completing {data.pipeline.length} queued course{data.pipeline.length === 1 ? "" : "s"} ({data.projected_uplift.trained_skills.join(", ")}):
              </p>
              {data.projected_uplift.uplifts.map((u) => (
                <div key={u.role} className="flex items-center justify-between border border-border px-3 py-2 bg-white">
                  <span className="text-[12px] text-slate-700">{u.role}</span>
                  <span className="text-[12px] font-mono">
                    {u.before}% <span className="text-teal-700 font-semibold">→ {u.after}%</span>
                    <span className="text-teal-700 ml-1.5">+{u.delta}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <SectionCard title="Learning Pipeline" icon={BookOpen} testId="growth-pipeline">
          {data.pipeline.length === 0 && <p className="text-[13px] text-muted-foreground">Nothing queued.</p>}
          <div className="divide-y divide-border">
            {data.pipeline.map((a) => (
              <div key={a.id} className="py-2.5 flex items-center gap-3">
                <div className="flex-1">
                  <a href={a.course_url} target="_blank" rel="noreferrer" className="text-[13px] font-medium text-slate-800 hover:text-teal-700">
                    {a.course_title}
                  </a>
                  <p className="text-[11px] text-muted-foreground">{a.partner} · focus: {a.focus_skill}</p>
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-1 border ${STATUS_CHIP[a.status]}`}>
                  {a.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Training History" testId="growth-history">
          {data.history.length === 0 && <p className="text-[13px] text-muted-foreground">No completed training yet.</p>}
          <div className="relative pl-5 space-y-4">
            <span className="absolute left-1 top-1 bottom-1 w-px bg-border" />
            {data.history.map((a) => (
              <div key={a.id} className="relative">
                <span className={`absolute -left-[18px] top-1.5 h-2 w-2 ${a.status === "certified" ? "bg-teal-600" : "bg-slate-400"}`} />
                <p className="text-[13px] font-medium text-slate-800">{a.course_title}</p>
                <p className="text-[11px] font-mono text-muted-foreground">
                  {a.partner} · {a.completed_at}{a.status === "certified" ? " · CERTIFIED" : ""}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Certification Wallet" icon={Award} testId="growth-cert-wallet">
        {data.certifications.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No certifications yet — certified courses appear here.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.certifications.map((a) => (
              <div key={a.id} className="border border-border bg-white p-4" data-testid={`cert-card-${a.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="h-4 w-4 text-teal-600" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-teal-700">Verified Certificate</span>
                </div>
                <p className="text-[13px] font-semibold text-[var(--brand-primary)] leading-snug">{a.course_title}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{a.partner}</p>
                <p className="text-[10px] font-mono text-slate-500 mt-2">{a.completed_at} · {a.certificate_id}</p>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
